import React, { useState } from 'react';
import { useTecnicos } from '../../hooks/useTecnicos';
import { saveTecnico, seedTecnicosDefault, DIAS_SEMANA } from '../../services/tecnicosService';

const COLOR_PRESETS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6B7280'];

const EMPTY = { employeeId: '', nombre: '', color: '#6B7280', activo: true, orden: 99, zonas: {} };

function TecnicoModal({ tecnico, onClose, onSaved }) {
  const [form, setForm]   = useState(() => ({ ...EMPTY, ...tecnico, zonas: { ...(tecnico?.zonas || {}) } }));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const isNew = !tecnico?.id;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setZona = (dia, txt) =>
    setForm(p => ({ ...p, zonas: { ...p.zonas, [dia]: txt.split(',').map(s => s.trim()).filter(Boolean) } }));

  const guardar = async () => {
    setSaving(true); setError('');
    try {
      await saveTecnico({ ...form, employeeId: Number(form.employeeId) || form.employeeId, orden: Number(form.orden) || 0 });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="font-bold text-gray-900 flex-1">{isNew ? 'Nuevo técnico' : `Editar · ${tecnico.nombre}`}</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">ID empleado (StelOrder) *</label>
              <input
                type="number" value={form.employeeId} disabled={!isNew}
                onChange={e => set('employeeId', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-red-300"
              />
              {!isNew && <p className="text-[11px] text-gray-400">El ID no se puede cambiar.</p>}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nombre *</label>
              <input
                type="text" value={form.nombre}
                onChange={e => set('nombre', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Color</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button key={c} onClick={() => set('color', c)}
                  className={`w-7 h-7 rounded-full border-2 transition ${form.color === c ? 'border-gray-800 scale-110' : 'border-white'}`}
                  style={{ backgroundColor: c }} />
              ))}
              <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                className="w-9 h-9 rounded cursor-pointer bg-transparent" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Orden</label>
              <input type="number" value={form.orden}
                onChange={e => set('orden', e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300" />
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm text-gray-700">
              <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} className="w-4 h-4" />
              Activo (en plantilla)
            </label>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zonas por día (separadas por coma)</label>
            {DIAS_SEMANA.map(dia => (
              <div key={dia} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-20 flex-shrink-0">{dia}</span>
                <input
                  type="text"
                  value={(form.zonas[dia] || []).join(', ')}
                  onChange={e => setZona(dia, e.target.value)}
                  placeholder="Ej: Zona 5"
                  className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300"
                />
              </div>
            ))}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

          <button onClick={guardar} disabled={saving}
            className="w-full py-3.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-2xl transition-colors">
            {saving ? 'Guardando…' : isNew ? 'Crear técnico' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TecnicosAdminView() {
  const { tecnicos, loading, fromFallback } = useTecnicos();
  const [modal, setModal]     = useState(null);   // null | {} | tecnico
  const [seeding, setSeeding] = useState(false);
  const [msg, setMsg]         = useState('');

  const sembrar = async () => {
    setSeeding(true); setMsg('');
    try {
      const n = await seedTecnicosDefault();
      setMsg(`✅ ${n} técnicos sembrados en Firestore. Ya puedes editarlos.`);
    } catch (e) {
      setMsg(`❌ ${e.message}`);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Técnicos</h1>
          <p className="text-sm text-gray-500">{tecnicos.length} en el roster · fuente única para rutas, comisiones, fichajes y flota</p>
        </div>
        <button
          onClick={() => setModal({})}
          className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors"
        >
          + Nuevo técnico
        </button>
      </div>

      {fromFallback && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <span className="text-xl">⚠️</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">Roster por defecto (desde el código)</p>
            <p className="text-xs text-amber-700 mt-0.5">
              La colección <code>tecnicos</code> de Firestore está vacía. Se está mostrando el roster
              hardcodeado. Siémbralo para poder editarlo desde aquí; el resto de la app seguirá
              funcionando igual.
            </p>
            <button
              onClick={sembrar} disabled={seeding}
              className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors"
            >
              {seeding ? 'Sembrando…' : 'Sembrar en Firestore'}
            </button>
          </div>
        </div>
      )}

      {msg && <p className="text-sm bg-gray-50 border rounded-xl px-3 py-2">{msg}</p>}

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Cargando roster…</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {tecnicos.map(t => (
            <div key={t.id} className={`bg-white rounded-2xl border shadow-sm p-4 ${!t.activo ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-3">
                <span className="w-4 h-4 rounded-full mt-0.5 flex-shrink-0" style={{ backgroundColor: t.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm">{t.nombre}</p>
                    <span className="text-xs text-gray-400 font-mono">#{t.employeeId}</span>
                    {!t.activo && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">Baja</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                    {DIAS_SEMANA.map(d => (
                      <div key={d}>
                        <span className="text-gray-400">{d}:</span> {(t.zonas[d] || []).join(', ') || '—'}
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => setModal(t)}
                  disabled={fromFallback}
                  title={fromFallback ? 'Siembra el roster primero' : 'Editar'}
                  className="text-xs font-semibold px-3 py-1.5 bg-gray-50 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 rounded-xl transition-colors flex-shrink-0"
                >
                  ✏️ Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== null && (
        <TecnicoModal
          tecnico={modal?.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => setMsg('✅ Guardado.')}
        />
      )}
    </div>
  );
}
